import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { TaskStatus, Task, ScoutTask, Stage, PathTest, Building, DriveTest } from '../../core/models';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BuildingDoc, BuildingService, LookupService, TaskService, UserService } from '../../core/services';


import { FesBaseComponent } from '../../shared/components/fes-base.component';
import { AppState, RoleActionPermissions, RoleMgr } from '../../core';
import { NgForm } from '@angular/forms';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { StageService } from '../../core/services/stage.service';
import { first, map, skip } from 'rxjs/operators';
import { PathTestDoc } from '../../core/schema/path-test-schema';
import { ScoutTaskDoc } from '../../core/schema/scout-task-schema';
import { getValueFromPath } from '../../shared/directives/list-view.directive';
import { DriveTestDoc } from '../../core/schema/drive-test-schema';

@Component({
  selector: 'app-task-edit',
  templateUrl: './task-edit.component.html',
  styleUrls: ['./task-edit.component.scss']
})
export class TaskEditComponent extends FesBaseComponent implements OnInit, OnDestroy  {

  static readonly DefaultExcludePattern = "(Pre|Dry)$"
  static readonly DefaultIncludePattern = ".*"

  @Input('taskDoc') set taskDocSetter( taskDoc : ScoutTaskDoc | PathTestDoc | DriveTestDoc ) {
    this.setTaskDoc(taskDoc)
  }

  @Input() doAfterSaveFn: () => void;

  @ViewChild('taskForm') taskForm: NgForm;
  
  taskDoc : ScoutTaskDoc | PathTestDoc | DriveTestDoc
  taskDocId : string
  taskStatus : TaskStatus

  building$: Observable<BuildingDoc>
  bLoadingBuilding = false

  buildingInfoArray : { id: string, name: string }[] = []
  userInfoArray : { id: string, name: string, role: string }[] = []  
  usersById = {}

  @Input() stages : string[] = []

  //used to reload Scout-list view
  onSaveScoutList = () => { }

  bNewDoc = false
  bSaving = false
  bLoadingTask = false
  bLoadingPointIds = false
  errorMsg : string = ''
  tmpTask: Task
  task$: Observable<ScoutTaskDoc|PathTestDoc|DriveTestDoc>
  currentUserId: string
  roleActionPermissions : {[roleId:string]: RoleActionPermissions } = {}
  includeRegExp = new RegExp(TaskEditComponent.DefaultIncludePattern)
  excludeRegExp = new RegExp(TaskEditComponent.DefaultExcludePattern)

  constructor( 
      appState : AppState,
      protected editModal : BsModalRef,
      private taskService: TaskService,
      private userService: UserService,
      private buildingService: BuildingService,
      public roleMgr: RoleMgr,
      private stageService : StageService,
      private lookupService : LookupService ) {
    super(appState)    
  }

  ngOnInit(): void {
    console.log(`ngOnInit() `)    

    
    this.currentUserId = this.appState.getUser()?._id
    this.roleMgr.getAllRoleActionPermissions().then( (roleActionPermissions: {[roleId:string]: RoleActionPermissions }) => {
      this.roleActionPermissions = roleActionPermissions
    })
    
    const stageConfig$ = this.lookupService.rxCollection.findOne('stage').$.pipe( 
      map(lookupDoc => lookupDoc?.values || [] as {id:string,value:string}[]),
      map(stageConfEntries => ({ 
        excludePattern: stageConfEntries.find(v=>v.id=='excludeStageNamesPattern')?.value,
        includePattern: stageConfEntries.find(v=>v.id=='includeStageNamesPattern')?.value
      }))
    );
    const stages$ = this.stageService.rxCollection.find().$

    /* Example couchdb lookup doc (must start with 1 or 2 and not end with Pre)
    {
      "_id": "stage",
      "values": [
        {"id": "includeStageNamesPattern","value": "^(1|2)"},
        {"id": "excludeStageNamesPattern","value": "(Pre)$"}
      ]
    }*/
    const filteredStages$ = combineLatest([stageConfig$,stages$]).pipe(
      map( ([config,stages]) => {
        try {
          this.excludeRegExp = new RegExp(config.excludePattern==null?TaskEditComponent.DefaultExcludePattern:config.excludePattern)
        } catch (error) {
          console.error(error)
          this.excludeRegExp = null
        }
        try {
          this.includeRegExp = new RegExp(config.includePattern==null?TaskEditComponent.DefaultIncludePattern:config.includePattern)
        } catch (error) {
          console.error(error)
          this.includeRegExp = null
        }
        // first apply include regex then exclude regex
        return stages.filter( stage => !this.includeRegExp || this.includeRegExp.test(stage.name) )
            .filter( stage => !this.excludeRegExp || !this.excludeRegExp.test(stage.name) )
      })
    )

    this.subscribe( filteredStages$, (stages) => {
      this.stages = stages.map( value => value.name) 
      this.addMissingStageName()
    })

    this.subscribe(this.userService.rxCollection.find().$, users => {
      this.userInfoArray = users.map( user => ({ id: user._id, name: `${user.firstName} ${user.lastName}`, role: user.role }))
      this.userInfoArray.sort( (a,b) => (a?.name > b?.name) ? 1 : -1)
      this.usersById = {}
      this.userInfoArray.forEach( u => this.usersById[u.id] = u)
    })

    this.subscribe(this.buildingService.rxCollection.find().$, buildings => {
      this.buildingInfoArray = buildings.map( (b:Building) => ({ id: b.id, name: b.commonName }))
      this.buildingInfoArray.sort( (a,b) => (a?.id > b?.id) ? 1 : -1)
    })    
  }
  
  taskDocSubscription: Subscription

  ngOnDestroy(): void {
    this.taskDocSubscription?.unsubscribe()
    this.destroy()
  }

  addMissingBuildingId = () => {
    console.log(`addMissingBuildingId()`)
    const selectedBuildingId = this.tmpTask?.buildingId || this.taskDoc?.buildingId
    if ( selectedBuildingId ) {  
      if ( this.buildingInfoArray?.length && !this.buildingInfoArray.find(b=>b.id==selectedBuildingId)) {
        this.buildingInfoArray.push( { id: selectedBuildingId, name: '?'})
      }
    }
  }

  addMissingStageName = () => {
    const selectedStageName = this.tmpTask?.stage || this.taskDoc?.stage
    if ( selectedStageName && this.stages && !this.stages.find(stageName => stageName == selectedStageName) ) {
      this.stages.push(selectedStageName)
    }
  }

  setTaskDoc( taskDoc: ScoutTaskDoc | PathTestDoc | DriveTestDoc ) {
    console.log(`setTaskDoc(${taskDoc?._id})`)

    this.taskDoc = taskDoc
    this.taskDocId = taskDoc?._id
    this.bNewDoc = taskDoc && !this.taskDocId
    this.taskDocSubscription?.unsubscribe()

    if ( !taskDoc ) {
      this.tmpTask = null
      return
    }

    this.taskDocSubscription = this.taskDoc.$.pipe(skip(1)).subscribe( updatedObj => {
      if ( updatedObj?._rev == this.tmpTask?._rev || this.bSaving ) {
        console.log('already on _rev=' + updatedObj._rev)
        return
      }
      this.tmpTask.status = updatedObj.status
      const controls = this.taskForm?.form?.controls
      console.dir(updatedObj)
      this.tmpTask = updatedObj

      // use new doc but keep existing dirty fields (things being edited by this user)
      if ( controls ) {
        const formFieldNames = [ 'stage', 'buildingId', 'schedule.scheduleRequestedDate', 'assignments.scheduleUserId',
            'schedule.scheduledDate', 'assignments.testUserId', 'schedule.scheduleNotes' ]
        let conflictValuesByName = {}
        for ( const name of formFieldNames ) {
          const control = controls[name]
          if ( control?.dirty ) {
            const objPath = name.split('.')
            let targetObj = getValueFromPath(this.tmpTask,objPath.slice(0,-1))
            if ( targetObj ) {
              conflictValuesByName[objPath.last()] = getValueFromPath(this.tmpTask,objPath)
              targetObj[objPath.last()] = control.value
            }
          }
        }
        if ( Object.keys(conflictValuesByName).length ) {
          this.appState.notifyWarning(`${updatedObj.type} changed and some edits are in progress: ${Object.keys(conflictValuesByName).join(',')}.`,this,5)
        }
      }
      console.dir(this.taskForm?.form)
    })

    this.addMissingStageName()
    this.addMissingBuildingId()

    if ( !taskDoc._id ) {
      // new doc
      if ( taskDoc.type == 'scout' ) {
        const scoutTaskDoc = taskDoc as ScoutTaskDoc
        if ( !scoutTaskDoc.scoutPaths ) {
          scoutTaskDoc.scoutPaths = []
        }
      } else if ( taskDoc.type == 'path test') {
        const pathTestDoc = taskDoc as PathTestDoc
        if ( !pathTestDoc.pathId ) {
          this.errorMsg = "Cannot edit new path test without pathId intialized"
        }
        if ( !pathTestDoc.testPoints ) {
          pathTestDoc.testPoints = []
        }
      } else if ( taskDoc.type == 'drive test') {
        const driveTestDoc = taskDoc as DriveTestDoc
        if ( !driveTestDoc.pathId ) {
          this.errorMsg = "Cannot edit new path test without pathId intialized"
        }
        if ( !driveTestDoc.testPoints ) {
          driveTestDoc.testPoints = []
        }
      }
    }

    this.tmpTask = taskDoc.toJSON()
    console.dir(this.tmpTask)
    if ( !this.tmpTask.assignments ) {
      this.tmpTask.assignments = {
        scheduleUserId: null,
        testUserId: null
      }
    }
    if ( !this.tmpTask.schedule ) {
      this.tmpTask.schedule = {
        scheduleRequestedDate: this.taskService.nowTimeStamp().substr(0,10),
        scheduleNotes: null,
        scheduleComments: null
      }
    } else if ( !this.tmpTask.schedule.scheduleRequestedDate && !this.tmpTask.schedule.scheduledDate) {
      this.tmpTask.schedule.scheduleRequestedDate = this.taskService.nowTimeStamp().substr(0,10)
    }
    if ( !this.tmpTask.cycle ) {
      this.tmpTask.cycle = 1
    }
    if ( !this.tmpTask.stage ) {
      this.tmpTask.stage = Stage.DefaultStageName
    }
    if ( this.tmpTask.buildingId ) {
      console.log('init building query with id ' + this.tmpTask.buildingId)
      this.building$ = this.buildingService.rxCollection.findOne(this.tmpTask.buildingId).$
    }
    console.dir(taskDoc.status)
    const statusId = TaskStatus.getLatestId(taskDoc.status)
    this.taskStatus = statusId ? { id: statusId, ...taskDoc.status[statusId] } : { id: 'none', timestamp: null, userId: null}
  }
  

  async onSave() {
    console.log('onSave()')
    console.log('form data before save:')
    console.dir(this.tmpTask)
    this.errorMsg = ''
    this.bSaving = true;

    try {
      if ( !this.tmpTask.status ) {
        //No status object yet, create it
        this.tmpTask.status = {
          scheduleRequested: {
            timestamp: new Date().toISOString(),
            userId: this.appState.getUser()._id
          }
        }
      }
      const formModel = this.tmpTask
      const patchData : Partial<Task> = { 
        stage: formModel.stage,
        buildingId: formModel.buildingId,
        status: formModel.status,
        assignments: formModel.assignments,
        schedule: formModel.schedule
      }

      // rxdb index on schedule so cannot be null
      if ( patchData.schedule && patchData.schedule.scheduledDate == null ) {
        patchData.schedule.scheduledDate = ""
      }

      switch( this.taskDoc.type ) {
        case 'scout':
          const scoutTaskDoc = this.taskDoc as ScoutTaskDoc
          if ( !scoutTaskDoc.scoutPaths ) {
            (patchData as Partial<ScoutTask>).scoutPaths = []
          }
          break;
        case 'path test':
          const pathTestDoc = this.taskDoc as PathTestDoc
          if ( !pathTestDoc.pathId ) {
            throw new Error("Cannot save new path test without pathId intialized")
          }
          if ( !pathTestDoc.testPoints ) {
            (patchData as Partial<PathTest>).testPoints = []
          }
          break;
        case 'drive test':
          const driveTestDoc = this.taskDoc as DriveTestDoc
          if ( !driveTestDoc.pathId ) {
            throw new Error("Cannot save new path test without pathId intialized")
          }
          if ( !driveTestDoc.testPoints ) {
            (patchData as Partial<DriveTest>).testPoints = []
          }
          break;
        default:
          throw new Error(`Unsupported task type '${this.taskDoc.type}'`)
      }
      console.log('pathData:')
      console.dir(patchData)

      let updatedDoc : ScoutTaskDoc  | PathTestDoc | DriveTestDoc
      if ( this.taskDoc._id ) {
        console.log(`Updating ${this.taskDoc.type} doc ${this.taskDoc._id}`)
        updatedDoc = await this.taskDoc.atomicPatch(patchData)
      } else {
        console.log(`Saving new ${this.taskDoc.type} doc`)
        Object.assign(this.taskDoc,patchData)
        const bSaved = await this.taskDoc.save()
        updatedDoc = this.taskDoc
      }
      this.tmpTask = updatedDoc.toJSON()
      console.log('form data after save:')
      console.dir(this.tmpTask)
      this.taskForm.form?.markAsPristine()
      const savedMsg = updatedDoc.type == 'scout' ?
        `Saved scout (building: ${updatedDoc.buildingId}, stage: ${updatedDoc.stage})` :
        `Saved path test (path: ${updatedDoc.pathId}, stage: ${updatedDoc.stage}, cycle: ${updatedDoc.cycle})`
      this.appState.notifySuccess(savedMsg,this,5)
    } catch (error) {
        this.errorMsg = this.taskService.extractErrorMsg(error)
        this.taskService.publishError(error, this)
    } finally {
        this.bSaving = false
    }
  }
  

  onClose() {
    this.editModal.hide()
  }

  async onBuildingIdChanged( buildingId : string ) {
    console.log(`onBuildingIdChanged(${buildingId})`)
    this.bLoadingBuilding = true
    this.building$ = this.buildingService.rxCollection.findOne(buildingId).$
    this.building$.pipe(first()).subscribe(building=>this.bLoadingBuilding = false)
  }
}
